import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { OrderDetailResponse, OrderSummary, OrderLineRequest } from '@shared/api-responses';
import type { OrderItem } from '@shared/types';
import { PROGRAM_LABELS } from '@shared/types';
import { apiFetch } from '../hooks/useApi';
import { orderBreakdown, formatSubmitted } from '../lib/orderBreakdown';

/** One checkbox row per line: qty, unit, item — each its own column. */
function Lines({ rows }: { rows: { key: string | number; qty: number; unit: string | null; name: string }[] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-black text-left text-xs uppercase">
          <th className="py-1 w-8" aria-label="Done" />
          <th className="py-1 w-14 text-right pr-3">Qty</th>
          <th className="py-1 w-20">Unit</th>
          <th className="py-1">Item</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-gray-300">
            <td className="py-2 w-8">☐</td>
            <td className="py-2 w-14 text-right font-mono pr-3">{r.qty}</td>
            <td className="py-2 w-20">{r.unit ?? ''}</td>
            <td className="py-2">{r.name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Printable view. Two different jobs on one sheet of paper:
 *
 *  1. The combined list you shop from — one row per item at its full quantity,
 *     because you buy the total in one trip.
 *  2. A page per station to drop off against, broken out by each request that
 *     station sent, so every submitted order can be checked off on its own.
 *
 * New orders come from a single request, so they print as one page with the
 * station and requester in the header -- the second half would only repeat the
 * first. Older orders that combined several requests keep the delivery pages.
 */
export default function OrderPrint() {
  const { id } = useParams();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [split, setSplit] = useState<OrderLineRequest[]>([]);

  useEffect(() => {
    apiFetch<OrderDetailResponse>(`/orders/${Number(id)}`).then((d) => {
      setOrder(d.order);
      setItems(d.items);
      setSplit(d.requestSplit);
    });
  }, [id]);

  if (!order) return <div className="p-6">Loading…</div>;

  const programLabel = PROGRAM_LABELS[order.program];
  const title =
    order.channel === 'vendor'
      ? `${order.vendor_name ?? 'Vendor'} Order — ${programLabel}`
      : `${programLabel} Shopping List`;

  const stations = orderBreakdown(items, split);

  // A line added during fulfillment has no requester, so it appears on the shop
  // list but on no delivery page. Call that out rather than let it vanish.
  const requested = new Set(split.map((s) => s.order_item_id));
  const unallocated = items.filter((i) => !requested.has(i.id));

  const single = stations.length === 1 && stations[0].requests.length === 1 ? stations[0] : null;
  const singleRequest = single?.requests[0];

  return (
    <div className="min-h-dvh bg-white text-black">
      <section className="p-8 max-w-2xl mx-auto">
        <div className="flex justify-between items-start border-b-2 border-black pb-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold uppercase">{title}</h1>
            {single && singleRequest ? (
              <>
                <p className="text-lg font-bold">{single.stationName}</p>
                <p className="text-sm">
                  Order #{order.id} · Request #{singleRequest.requestId} · {singleRequest.submitterName ?? 'Unknown'} ·{' '}
                  {formatSubmitted(singleRequest.submittedAt)}
                </p>
              </>
            ) : (
              <p className="text-sm">
                Order #{order.id} · DCVFD {programLabel}
                {stations.length > 0 && ` · ${stations.length} station${stations.length === 1 ? '' : 's'}`}
              </p>
            )}
          </div>
          <button onClick={() => window.print()} className="no-print border border-black px-3 py-1 text-sm">
            Print
          </button>
        </div>
        {!single && <h2 className="text-xs font-bold uppercase tracking-wide mb-1">Shop this — full order</h2>}
        <Lines rows={items.map((i) => ({ key: i.id, qty: i.qty, unit: i.unit, name: i.item_name }))} />
      </section>

      {!single &&
        stations.map((st) => (
          <section key={st.stationId} className="p-8 max-w-2xl mx-auto break-before-page">
            <div className="border-b-2 border-black pb-3 mb-4">
              <h1 className="text-2xl font-bold uppercase">{st.stationName}</h1>
              <p className="text-sm">
                Deliver from order #{order.id} · {st.requests.length} request{st.requests.length === 1 ? '' : 's'}
              </p>
            </div>
            {st.requests.map((rq) => (
              <div key={rq.requestId} className="mb-6 break-inside-avoid">
                <h2 className="text-sm font-bold">
                  Request #{rq.requestId} · {rq.submitterName ?? 'Unknown'} · {formatSubmitted(rq.submittedAt)}
                </h2>
                <Lines rows={rq.lines.map((l) => ({ key: l.orderItemId, qty: l.qty, unit: l.unit, name: l.name }))} />
              </div>
            ))}
          </section>
        ))}

      {!single && stations.length > 0 && unallocated.length > 0 && (
        <section className="p-8 max-w-2xl mx-auto break-before-page">
          <div className="border-b-2 border-black pb-3 mb-4">
            <h1 className="text-2xl font-bold uppercase">Unassigned</h1>
            <p className="text-sm">Added during fulfillment — nobody requested these, so decide where they go.</p>
          </div>
          <Lines rows={unallocated.map((i) => ({ key: i.id, qty: i.qty, unit: i.unit, name: i.item_name }))} />
        </section>
      )}
    </div>
  );
}
